import { useEffect, useState } from "react";
import { Grid3x3, CheckCircle2, XCircle, AlertTriangle, FileDown, Sparkles, Loader2, FileText } from "lucide-react";
import { api, API, apiError } from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { PageHero } from "@/components/PageHero";
import { Markdown } from "@/components/Markdown";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

export default function Coverage() {
  const { tenantId, organizations, currentOrg } = useTenant();
  const { user } = useAuth();
  const isConsultant = user?.role === "consultant";
  const [schemes, setSchemes] = useState([]);
  const [schemeId, setSchemeId] = useState("");
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({ open: false, loading: false, text: "", req: null, creating: false });
  const [linking, setLinking] = useState(false);
  const [rationales, setRationales] = useState({});
  const fetchRationale = async (row, d) => {
    const key = `${row.requirement_id}:${d.id}`;
    if (rationales[key]?.text) { setRationales((s) => ({ ...s, [key]: { ...s[key], open: !s[key].open } })); return; }
    setRationales((s) => ({ ...s, [key]: { loading: true, open: true } }));
    try {
      const r = await api.get(`/coverage/rationale?tenant_id=${tenantId}&requirement_id=${row.requirement_id}&doc_id=${d.id}`);
      setRationales((s) => ({ ...s, [key]: { loading: false, open: true, text: r.data.text } }));
    } catch (e) {
      setRationales((s) => ({ ...s, [key]: { loading: false, open: true, text: "Motivazione non disponibile." } }));
    }
  };
  const loadCov = () => {
    if (tenantId && schemeId) api.get(`/coverage?tenant_id=${tenantId}&scheme_id=${schemeId}`).then((r) => setData(r.data));
    else setData(null);
  };
  const autoLink = async () => {
    if (!tenantId) return;
    setLinking(true);
    try {
      await api.post(`/clients/${tenantId}/auto-link`);
      toast.info("Steve sta analizzando i documenti e associando i punti norma…");
      const poll = setInterval(async () => {
        try {
          const s = await api.get(`/clients/${tenantId}/auto-link-status`);
          if (s.data.status === "done") {
            clearInterval(poll); setLinking(false);
            toast.success(`Collegamento completato: ${s.data.linked} documenti associati ai punti norma`);
            loadCov();
          } else if (s.data.status === "error") {
            clearInterval(poll); setLinking(false);
            toast.error(s.data.detail || "Collegamento non riuscito");
          }
        } catch (e) { /* keep polling */ }
      }, 4000);
    } catch (e) { setLinking(false); toast.error(apiError(e)); }
  };
  const genDraft = async (row) => {
    setDraft({ open: true, loading: true, text: "", req: row });
    try {
      const r = await api.post(`/insights/gap-draft?requirement_id=${row.requirement_id}&tenant_id=${tenantId}`);
      setDraft((s) => ({ ...s, loading: false, text: r.data.draft }));
    } catch (e) { setDraft((s) => ({ ...s, loading: false, text: "Impossibile generare la bozza." })); }
  };
  const createDoc = async () => {
    if (!draft.req || !draft.text) return;
    setDraft((s) => ({ ...s, creating: true }));
    try {
      const r = await api.post(`/insights/gap-draft/document?requirement_id=${draft.req.requirement_id}&tenant_id=${tenantId}`, { content: draft.text });
      toast.success(`Documento ${r.data.codice} creato e collegato al requisito ${draft.req.codice}`);
      setDraft({ open: false, loading: false, text: "", req: null, creating: false });
      loadCov();
    } catch (e) { setDraft((s) => ({ ...s, creating: false })); toast.error(apiError(e)); }
  };

  useEffect(() => {
    api.get("/schemes").then((r) => {
      const all = r.data;
      const nk = (s) => (s || "").toLowerCase().split(":")[0].replace(/\s+/g, "");
      let allowed = [];
      if (currentOrg?.scheme_ids && currentOrg.scheme_ids.length) {
        allowed = all.filter((s) => currentOrg.scheme_ids.includes(s.id));
      } else if (currentOrg?.norme_accreditate && currentOrg.norme_accreditate.length) {
        const targets = currentOrg.norme_accreditate.map(nk);
        allowed = all.filter((s) => targets.some((t) => { const k = nk(s.sigla); return k === t || k.startsWith(t) || t.startsWith(k); }));
      }
      const list = allowed.length ? allowed : (currentOrg ? [] : all);
      setSchemes(list);
      setSchemeId(list.length ? list[0].id : "");
    });
  }, [currentOrg]);
  useEffect(() => { loadCov(); }, [tenantId, schemeId]);

  return (
    <div className="space-y-6">
      <PageHero label="Gap analysis" title="Copertura requisiti" subtitle="Quali requisiti sono coperti da almeno un documento del cliente." />

      <div className="flex flex-wrap items-end gap-3 sm:gap-4">
        <div className="min-w-0 flex-1 sm:flex-none">
          <div className="label-caps mb-1.5">Cliente</div>
          <div className="text-sm font-medium h-9 flex items-center px-3 rounded-md border border-slate-200 bg-slate-50 w-full sm:min-w-[200px] truncate">
            {currentOrg?.nome || "Seleziona un cliente in alto"}
          </div>
        </div>
        <div className="min-w-0">
          <div className="label-caps mb-1.5">Schema</div>
          <Select value={schemeId} onValueChange={setSchemeId}>
            <SelectTrigger className="w-44 sm:w-56" data-testid="coverage-scheme"><SelectValue placeholder="Schema" /></SelectTrigger>
            <SelectContent>{schemes.map((s) => <SelectItem key={s.id} value={s.id}>{s.sigla}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {tenantId && isConsultant && (
          <Button data-testid="auto-link-btn" onClick={autoLink} disabled={linking}
            className="bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white">
            {linking ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
            {linking ? "Collegamento in corso…" : "Collega requisiti (AI)"}
          </Button>
        )}
        {tenantId && data && data.total > 0 && (
          <Button variant="outline" data-testid="download-report-btn"
            onClick={() => window.open(`${API}/coverage/report?tenant_id=${tenantId}&scheme_id=${schemeId}`, "_blank")}>
            <FileDown className="h-4 w-4 mr-2" /> Scarica report PDF
          </Button>
        )}
      </div>

      {!tenantId ? (
        <Card className="p-12 text-center border-dashed">
          <Grid3x3 className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Seleziona un cliente dal menu in alto per calcolare la copertura.</p>
        </Card>
      ) : !data ? null : (
        <>
          <div className="rounded-2xl p-5 bg-gradient-to-br from-[#0A1734] via-[#0A1027] to-[#06090F] border border-white/10 shadow-xl relative overflow-hidden" data-testid="coverage-deck">
            <div className="pointer-events-none absolute -top-16 -right-10 h-48 w-48 rounded-full bg-indigo-600/20 blur-3xl" />
            <div className="relative grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="rounded-xl bg-white/[0.05] border border-white/10 p-5">
                <div className="font-mono text-[11px] uppercase tracking-wider text-slate-400">Copertura</div>
                <div className="font-display text-3xl font-bold mt-1.5 text-white" data-testid="coverage-percent">{data.percent}%</div>
                <div className="mt-3 h-2 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-gradient-to-r from-cyan-400 to-indigo-400" style={{ width: `${data.percent}%` }} /></div>
              </div>
              <div className="rounded-xl bg-white/[0.05] border border-white/10 p-5">
                <div className="font-mono text-[11px] uppercase tracking-wider text-cyan-300">Coperti</div>
                <div className="font-display text-3xl font-bold mt-1.5 text-white">{data.covered}<span className="text-lg text-slate-400"> / {data.total}</span></div>
              </div>
              <div className="rounded-xl bg-white/[0.05] border border-white/10 p-5">
                <div className="font-mono text-[11px] uppercase tracking-wider text-amber-300">Scoperti</div>
                <div className="font-display text-3xl font-bold mt-1.5 text-amber-300">{data.uncovered}</div>
              </div>
            </div>
          </div>

          <Card className="border-slate-200 overflow-hidden">
            <div className="hidden md:grid grid-cols-[100px_1fr_auto] gap-4 px-5 py-3 border-b border-slate-200 bg-slate-50 label-caps">
              <div>Codice</div><div>Requisito</div><div>Copertura</div>
            </div>
            <div className="divide-y divide-slate-100" data-testid="coverage-matrix">
              {data.rows.map((row) => (
                <div key={row.requirement_id} data-testid={`coverage-row-${row.requirement_id}`}
                  className={`grid grid-cols-1 md:grid-cols-[100px_1fr_auto] gap-2 md:gap-4 px-4 md:px-5 py-3 items-start ${row.covered ? "" : "bg-red-50/40"}`}>
                  <Badge variant="outline" className="font-mono w-fit">{row.codice}</Badge>
                  <div>
                    <div className="text-sm font-medium">{row.titolo}</div>
                    {row.documents.length > 0 && (
                      <div className="mt-1.5 space-y-1.5">
                        {row.documents.map((d) => {
                          const key = `${row.requirement_id}:${d.id}`;
                          const rat = rationales[key];
                          return (
                            <div key={d.id}>
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <Badge className="bg-blue-100 text-blue-800 border-blue-200 font-mono text-[11px]">{d.codice}</Badge>
                                <span className="text-xs text-slate-500 truncate max-w-[220px]">{d.titolo}</span>
                                <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px] text-indigo-700 hover:bg-indigo-50"
                                  onClick={() => fetchRationale(row, d)} data-testid={`rationale-${row.requirement_id}-${d.id}`}>
                                  {rat?.loading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1" />} Perché copre?
                                </Button>
                              </div>
                              {rat?.open && rat?.text && (
                                <p className="text-xs text-slate-600 bg-indigo-50/60 border border-indigo-100 rounded px-2 py-1.5 mt-1" data-testid={`rationale-text-${row.requirement_id}-${d.id}`}>{rat.text}</p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div>
                    {row.covered
                      ? <span className="inline-flex items-center gap-1 text-blue-700 text-sm font-medium"><CheckCircle2 className="h-4 w-4" />Coperto</span>
                      : (
                        <div className="flex flex-col items-end gap-1.5">
                          <span className="inline-flex items-center gap-1 text-red-600 text-sm font-medium"><XCircle className="h-4 w-4" />Scoperto</span>
                          {isConsultant && (
                            <Button size="sm" variant="outline" className="h-7 text-indigo-700 border-indigo-200 hover:bg-indigo-50" onClick={() => genDraft(row)} data-testid={`gap-draft-${row.requirement_id}`}>
                              <Sparkles className="h-3.5 w-3.5 mr-1" /> Bozza AI
                            </Button>
                          )}
                        </div>
                      )}
                  </div>
                </div>
              ))}
              {data.rows.length === 0 && <p className="text-sm text-slate-500 p-6">Nessun requisito registrato per questo schema.</p>}
            </div>
          </Card>
        </>
      )}

      <Dialog open={draft.open} onOpenChange={(o) => setDraft((s) => ({ ...s, open: o }))}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="gap-draft-dialog">
          <DialogHeader><DialogTitle className="text-base">Bozza AI · {draft.req?.codice} {draft.req?.titolo}</DialogTitle></DialogHeader>
          {draft.loading
            ? <div className="py-10 text-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />Steve sta preparando la proposta...</div>
            : <Markdown>{draft.text}</Markdown>}
          {!draft.loading && draft.text && draft.text !== "Impossibile generare la bozza." && (
            <div className="flex justify-end gap-2 pt-4 border-t border-slate-200 mt-4">
              <Button variant="outline" onClick={() => setDraft((s) => ({ ...s, open: false }))} data-testid="draft-cancel-btn">Chiudi</Button>
              <Button onClick={createDoc} disabled={draft.creating} data-testid="draft-create-doc-btn"
                className="bg-gradient-to-r from-blue-600 to-indigo-500 hover:from-blue-700 hover:to-indigo-600 text-white">
                {draft.creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
                Crea documento e collega
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
